'use client';

import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useLayoutEffect,
} from 'react';
import { useParams, usePathname, useRouter } from 'next/navigation';
import { signIn, useSession } from 'next-auth/react';
import { AUTH_PROVIDER, SESSION_STATUS } from '@/constants/auth';
import { useCurrentSociety } from '@/hooks/publicSearch/useCurrentSociety';
import usePublicSearchLimit from '@/components/publicSearch/heroSection/usePublicSearchLimit';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import SocietyNotFoundPage from '@/components/publicSearch/societyNotFound/SocietyNotFoundPage';
import SocietyLoadingScreen from '@/components/publicSearch/societyNotFound/SocietyLoadingScreen';
import {
  buildPageTitle,
  resolveSocietySectionLabel,
} from '@/lib/societyPageTitle';

const isLinkI4Path = (pathname: string) =>
  pathname.includes('/external/link-i4');

export default function SocietyLayout({ children }: PropsWithChildren) {
  const society = useCurrentSociety();
  const router = useRouter();
  const { status: sessionStatus } = useSession();
  const sessionLoading = sessionStatus === SESSION_STATUS.LOADING;
  const isAuthenticated = sessionStatus === SESSION_STATUS.AUTHENTICATED;
  const setSociety = usePublicSearchContext((context) => context.setSociety);
  const storedSocietyCode = usePublicSearchContext(
    (context) => context.society.societyCode ?? '',
  );
  const storedSocietyName = usePublicSearchContext(
    (context) => context.society.name?.trim() ?? '',
  );
  const pathname = usePathname() ?? '';
  const orgNameParam = useParams<{ orgName?: string | string[] }>().orgName;
  const urlSocietyCode =
    (Array.isArray(orgNameParam) ? orgNameParam[0] : orgNameParam)?.trim() ??
    '';

  usePublicSearchLimit();

  const societyCodesMatch = (a: string, b: string) =>
    a.toUpperCase() === b.toUpperCase();

  const onLinkPage = isLinkI4Path(pathname);
  const linkTarget = urlSocietyCode
    ? `/${urlSocietyCode}/external/link-i4`
    : null;

  const isResolvingSociety =
    Boolean(urlSocietyCode) &&
    !societyCodesMatch(storedSocietyCode, urlSocietyCode);

  const societyPageTitleName =
    (!society.loading && !society.notFound && society.name?.trim()) ||
    storedSocietyName;

  const sectionLabel = resolveSocietySectionLabel(pathname);

  const applySocietyDocumentTitle = useCallback(() => {
    if (
      society.notFound ||
      isResolvingSociety ||
      !societyPageTitleName ||
      !urlSocietyCode
    ) {
      return;
    }
    document.title = buildPageTitle({
      label: sectionLabel,
      societyName: societyPageTitleName,
    });
  }, [
    society.notFound,
    isResolvingSociety,
    societyPageTitleName,
    urlSocietyCode,
    sectionLabel,
  ]);

  // Set the tab title for every route under /{societyCode}/ (bare /{code}
  // redirect, animals, members, etc.). pathname is a dep so we re-apply after
  // client navigations that reset document.title to root metadata even when
  // the resolved section label is unchanged.
  useLayoutEffect(() => {
    applySocietyDocumentTitle();
  }, [applySocietyDocumentTitle, pathname]);

  useEffect(() => {
    applySocietyDocumentTitle();
  }, [applySocietyDocumentTitle, pathname]);

  useEffect(() => {
    if (!urlSocietyCode) return;
    const current = usePublicSearchContext.getState().society;
    const storedCode = current.societyCode ?? '';
    if (!societyCodesMatch(urlSocietyCode, storedCode)) {
      // Keep URL societyCode in context so link-i4 and isResolvingSociety work
      // while society/access queries are still in flight.
      setSociety({
        loading: true,
        orgId: '',
        societyCode: urlSocietyCode,
        notFound: false,
      });
    }
  }, [urlSocietyCode, setSociety]);

  // Private society: login first (return to this page), or send to link-i4 only when
  // authenticated and still not linked.
  useEffect(() => {
    if (
      society.loading ||
      sessionLoading ||
      !society.requiresLink ||
      onLinkPage ||
      !linkTarget
    ) {
      return;
    }

    if (!isAuthenticated) {
      void signIn(AUTH_PROVIDER, { callbackUrl: pathname });
      return;
    }

    setSociety({
      loading: false,
      orgId: society.orgId ?? '',
      societyCode: urlSocietyCode,
      name: society.name,
      notFound: false,
    });
    router.replace(linkTarget);
  }, [
    society.loading,
    society.requiresLink,
    society.orgId,
    society.name,
    sessionLoading,
    isAuthenticated,
    onLinkPage,
    linkTarget,
    pathname,
    router,
    urlSocietyCode,
    setSociety,
  ]);

  useEffect(() => {
    if (society.loading || sessionLoading) return;

    if (society.requiresLink) {
      // Prime URL society so link-i4 can render and isResolvingSociety clears.
      setSociety({
        loading: false,
        orgId: society.orgId ?? '',
        societyCode: urlSocietyCode || (society.societyCode ?? ''),
        name: society.name,
        notFound: false,
      });
      return;
    }

    if (society.notFound) {
      setSociety({ loading: false, orgId: '', notFound: true });
      return;
    }

    if (!society.orgId) {
      setSociety({
        loading: false,
        orgId: '',
        societyCode: urlSocietyCode,
        notFound: false,
      });
      return;
    }

    setSociety(society);
  }, [society, setSociety, urlSocietyCode, sessionLoading]);

  if (society.loading || sessionLoading) {
    return <SocietyLoadingScreen />;
  }

  if (society.requiresLink && !onLinkPage) {
    return <SocietyLoadingScreen />;
  }

  if (society.notFound) {
    return <SocietyNotFoundPage />;
  }

  if (isResolvingSociety && !onLinkPage) {
    return <SocietyLoadingScreen />;
  }

  return children;
}
